import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Upload, Eye, Download, Trash2, Edit2, 
  Save, X, Loader2, AlertTriangle, CheckCircle, FileText, QrCode, ZoomIn
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Bank Details Card Component
 * Displays and manages bank details for a lead including PDF upload + QR code
 */
const BankDetailsCard = ({ leadId, initialData = {}, onSaved, headerExtra = null, editAllowed = true }) => {
  const { token } = useAuth();
  const [bankDetails, setBankDetails] = useState(initialData);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  // Mega-Spec Item 2 — QR code state
  const [qrUploading, setQrUploading] = useState(false);
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [qrZoomOpen, setQrZoomOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setBankDetails(initialData);
      setFormData(initialData);
    }
  }, [initialData]);

  // Load the QR image (auth-protected) as a blob URL whenever a QR exists.
  useEffect(() => {
    let revoke = null;
    const loadQr = async () => {
      if (!bankDetails.qrCodeUrl) { setQrBlobUrl(null); return; }
      try {
        const response = await fetch(`${API_BASE}/api/leads/${leadId}/bank-qr`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) { setQrBlobUrl(null); return; }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        revoke = url;
        setQrBlobUrl(url);
      } catch {
        setQrBlobUrl(null);
      }
    };
    loadQr();
    return () => { if (revoke) window.URL.revokeObjectURL(revoke); };
  }, [bankDetails.qrCodeUrl, leadId, token]);

  const handleQrUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['.jpg', '.jpeg', '.png', '.webp'];
    const lower = file.name.toLowerCase();
    if (!allowed.some(ext => lower.endsWith(ext))) {
      setMessage({ type: 'error', text: 'Only JPG, PNG or WEBP images are allowed' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size exceeds 10MB limit' });
      return;
    }
    try {
      setQrUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/upload-bank-qr`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!response.ok) {
        const errText = await response.text();
        let errMsg = 'Failed to upload QR code';
        try { errMsg = JSON.parse(errText)?.detail || errMsg; } catch { errMsg = errText || errMsg; }
        throw new Error(errMsg);
      }
      const data = await response.json();
      setBankDetails(prev => ({ ...prev, qrCodeUrl: data.url, qrCodeName: data.filename }));
      setMessage({ type: 'success', text: 'QR code uploaded successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setQrUploading(false);
    }
  };

  const handleDeleteQr = async () => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/bank-qr`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete QR code');
      setBankDetails(prev => ({ ...prev, qrCodeUrl: null, qrCodeName: null }));
      setQrBlobUrl(null);
      setMessage({ type: 'success', text: 'QR code deleted successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // iter311 — Never send PDF/QR/attachment metadata from the edit
      // form; the backend now writes those via dedicated upload
      // endpoints and preserves them across bank-detail text edits.
      // Explicitly filter the payload so we can't accidentally blank
      // out `cancelledChequePdfPath` etc. even if the form state ever
      // ends up carrying those keys.
      const editableFields = [
        'accountHolderName', 'bankName', 'accountNumber',
        'ifscCode', 'branchName', 'upiId',
      ];
      const payload = {};
      for (const key of editableFields) {
        if (key in formData) payload[key] = formData[key];
      }
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/bank-details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to save bank details');

      const data = await response.json();
      setBankDetails(data);
      setEditing(false);
      setMessage({ type: 'success', text: 'Bank details saved successfully!' });
      // Item 9 — notify parent so the Property Checklist "Bank Details"
      // status badge refreshes instantly (backend auto-completes it).
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setMessage({ type: 'error', text: 'Only PDF files are allowed' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size exceeds 10MB limit' });
      return;
    }

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_BASE}/api/leads/${leadId}/upload-bank-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (!response.ok) {
        const errText = await response.text();
        let errMsg = 'Failed to upload PDF';
        try { errMsg = JSON.parse(errText)?.detail || errMsg; } catch { errMsg = errText || errMsg; }
        throw new Error(errMsg);
      }

      const data = await response.json();
      setBankDetails(prev => ({
        ...prev,
        cancelledChequePdfUrl: data.url,
        cancelledChequePdfName: data.filename
      }));
      setMessage({ type: 'success', text: 'PDF uploaded successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('Are you sure you want to delete this PDF?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/bank-pdf`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete PDF');

      setBankDetails(prev => ({
        ...prev,
        cancelledChequePdfUrl: null,
        cancelledChequePdfName: null
      }));
      setMessage({ type: 'success', text: 'PDF deleted successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async () => {
    // iter311 — Open the new tab SYNCHRONOUSLY (before any `await`) so
    // the popup opens as a direct consequence of the user's click.
    // Otherwise every mainstream browser blocks the tab as a popup
    // because the async gap between click and window.open breaks the
    // user-gesture chain. We navigate the placeholder tab to the blob
    // URL once the fetch resolves.
    const popup = window.open('', '_blank');
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/bank-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        if (popup) popup.close();
        setMessage({ type: 'error', text: 'Session expired. Please login again.' });
        return;
      }

      if (!response.ok) {
        if (popup) popup.close();
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (popup) {
        popup.location.href = url;
      } else {
        // Popup was blocked despite the sync open — fall back to the
        // legacy async call so users still get the PDF, just with a
        // browser-blocker warning banner.
        window.open(url, '_blank');
      }
      // Clean up after a delay to allow the new tab to load
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      if (popup) popup.close();
      setMessage({ type: 'error', text: err.message || 'Failed to view PDF' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/bank-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        setMessage({ type: 'error', text: 'Session expired. Please login again.' });
        return;
      }
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = bankDetails.cancelledChequePdfName || 'cancelled_cheque.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'PDF downloaded successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to download PDF' });
    } finally {
      setLoading(false);
    }
  };

  // Clear message after timeout
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="bank-details-card">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard size={18} className="text-teal-600" />
          <h3 className="font-semibold text-gray-800">Bank Details</h3>
        </div>
        {!editing && editAllowed && (
          <div className="flex items-center gap-2">
            {headerExtra}
            <button
              onClick={() => setEditing(true)}
              className="text-teal-600 hover:text-teal-800 p-1"
              data-testid="edit-bank-details-btn"
            >
              <Edit2 size={16} />
            </button>
          </div>
        )}
        {!editing && !editAllowed && (
          <div className="flex items-center gap-2">
            {headerExtra}
          </div>
        )}
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`mx-4 mt-4 flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
          message.type === 'error'
            ? 'bg-red-50 text-red-600'
            : 'bg-green-50 text-green-600'
        }`}>
          {message.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {editing ? (
          /* Edit Mode */
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={formData.accountHolderName || ''}
                  onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="bank-holder-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={formData.bankName || ''}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="bank-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.accountNumber || ''}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="bank-account-number-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IFSC Code
                </label>
                <input
                  type="text"
                  value={formData.ifscCode || ''}
                  onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="bank-ifsc-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch Name
                </label>
                <input
                  type="text"
                  value={formData.branchName || ''}
                  onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="bank-branch-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  UPI ID
                </label>
                <input
                  type="text"
                  value={formData.upiId || ''}
                  onChange={(e) => setFormData({ ...formData, upiId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="example@upi"
                  data-testid="bank-upi-input"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                data-testid="save-bank-details-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save
              </button>
              <button
                onClick={() => { setEditing(false); setFormData(bankDetails); }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* View Mode */
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoItem label="Account Holder Name" value={bankDetails.accountHolderName} />
              <InfoItem label="Bank Name" value={bankDetails.bankName} />
              <InfoItem label="Account Number" value={bankDetails.accountNumber ? '****' + bankDetails.accountNumber.slice(-4) : null} />
              <InfoItem label="IFSC Code" value={bankDetails.ifscCode} />
              <InfoItem label="Branch Name" value={bankDetails.branchName} />
              <InfoItem label="UPI ID" value={bankDetails.upiId} />
            </div>

            {/* Cancelled Cheque PDF Section */}
            <div className="pt-4 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Cancelled Cheque PDF</h4>
              
              {bankDetails.cancelledChequePdfUrl ? (
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <FileText size={20} className="text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {bankDetails.cancelledChequePdfName || 'cancelled_cheque.pdf'}
                      </p>
                      <p className="text-xs text-gray-500">PDF Document</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleViewPdf}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="View PDF"
                      data-testid="view-pdf-btn"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={handleDownloadPdf}
                      className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg"
                      title="Download PDF"
                      data-testid="download-pdf-btn"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={handleDeletePdf}
                      disabled={loading}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete PDF"
                      data-testid="delete-pdf-btn"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="upload-pdf-input"
                  />
                  {uploading ? (
                    <Loader2 size={20} className="animate-spin text-teal-600" />
                  ) : (
                    <Upload size={20} className="text-gray-400" />
                  )}
                  <span className="text-sm text-gray-500">
                    {uploading ? 'Uploading...' : 'Click to upload cancelled cheque PDF (Max 10MB)'}
                  </span>
                </label>
              )}
            </div>

            {/* Mega-Spec Item 2 — Payment QR Code Section */}
            <div className="pt-4 border-t border-gray-100" data-testid="bank-qr-section">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <QrCode size={16} className="text-teal-600" /> Payment QR Code
              </h4>

              {bankDetails.qrCodeUrl ? (
                <div className="space-y-3">
                  <div
                    className="relative inline-block group cursor-zoom-in border border-gray-200 rounded-xl p-3 bg-white"
                    onClick={() => setQrZoomOpen(true)}
                    data-testid="bank-qr-preview"
                  >
                    {qrBlobUrl ? (
                      <img
                        src={qrBlobUrl}
                        alt="Payment QR Code"
                        className="w-56 h-56 object-contain"
                        data-testid="bank-qr-image"
                      />
                    ) : (
                      <div className="w-56 h-56 flex items-center justify-center bg-gray-50 rounded-lg">
                        <Loader2 size={24} className="animate-spin text-teal-500" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 rounded-xl transition-colors">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                        <ZoomIn size={18} className="text-gray-700" />
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQrZoomOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50"
                      data-testid="bank-qr-zoom-btn"
                    >
                      <ZoomIn size={14} /> Zoom / Scan
                    </button>
                    <button
                      onClick={handleDeleteQr}
                      disabled={loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                      data-testid="bank-qr-delete-btn"
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    onChange={handleQrUpload}
                    className="hidden"
                    disabled={qrUploading}
                    data-testid="bank-qr-upload-input"
                  />
                  {qrUploading ? (
                    <Loader2 size={20} className="animate-spin text-teal-600" />
                  ) : (
                    <Upload size={20} className="text-gray-400" />
                  )}
                  <span className="text-sm text-gray-500">
                    {qrUploading ? 'Uploading...' : 'Click to upload QR code (JPG/PNG/WEBP, Max 10MB)'}
                  </span>
                </label>
              )}
            </div>
          </div>
        )}
      </div>

      {/* QR Code Zoom Modal */}
      {qrZoomOpen && qrBlobUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setQrZoomOpen(false)}
          data-testid="bank-qr-zoom-modal"
        >
          <div className="relative bg-white rounded-2xl p-4 max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setQrZoomOpen(false)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg border border-gray-200 hover:bg-gray-50"
              data-testid="bank-qr-zoom-close"
            >
              <X size={18} className="text-gray-600" />
            </button>
            <img
              src={qrBlobUrl}
              alt="Payment QR Code (enlarged)"
              className="max-w-[80vw] max-h-[80vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for info display
const InfoItem = ({ label, value }) => (
  <div className="flex py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 w-36 flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-900">{value || '-'}</span>
  </div>
);

export default BankDetailsCard;
